import { useEffect, useRef, useState } from 'react'
import { canSpeak, isMuted, onMuteChange, speak, stopSpeaking } from '../lib/speech.js'
import { track } from '../lib/analytics.js'
import { registerReader } from '../lib/hotkey.js'

/**
 * A block of text plus its own read-aloud button, highlighting each word as it
 * is spoken (SPEC Section 7: read-aloud on every text block).
 *
 * `auto` starts on mount — used at ages 2 and 4, where the kid cannot read the
 * button, let alone the text.
 *
 * The space bar does what the button does (D-112): the block registers its
 * toggle with the read-aloud key while it is mounted.
 */
export default function ReadAloud({ text, auto = false, as: Tag = 'p', className = '', size = 'md', children, extraSpoken = '' }) {
  const [range, setRange] = useState(null) // [start, end) into `text`
  const [busy, setBusy] = useState(false)
  const [muted, setMutedState] = useState(isMuted())
  const token = useRef(0)

  useEffect(() => onMuteChange(setMutedState), [])

  const run = async () => {
    const mine = ++token.current
    setBusy(true)
    await speak(`${text} ${extraSpoken}`.trim(), {
      onWord: (i, len) => {
        if (token.current === mine && i < text.length) setRange([i, i + (len || wordLenAt(text, i))])
      },
    })
    if (token.current === mine) {
      setBusy(false)
      setRange(null)
    }
  }

  const toggle = (via = 'tap') => {
    if (busy) {
      token.current++
      stopSpeaking()
      setBusy(false)
      setRange(null)
    } else {
      track('readaloud_tap', { via }) // a deliberate "read it to me"; auto-reads are not counted
      run()
    }
  }

  // A ref, so the key always reaches the latest toggle without re-registering
  // on every render; registered once per mount, in mount order.
  const latest = useRef(toggle)
  latest.current = toggle
  useEffect(() => registerReader((via) => latest.current(via)), [])

  useEffect(() => {
    setRange(null)
    setBusy(false)
    token.current++
    if (auto && !isMuted() && canSpeak() && text) run()
    return () => {
      token.current++
      stopSpeaking()
    }
  }, [text, auto]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Tag className={`readaloud ${className}`}>
      <span className="readaloud__text">
        {range ? (
          <>
            {text.slice(0, range[0])}
            <mark className="readaloud__word">{text.slice(range[0], range[1])}</mark>
            {text.slice(range[1])}
          </>
        ) : (
          text
        )}
        {children}
      </span>
      {canSpeak() && !muted && (
        <button className={`speaker speaker--${size}${busy ? ' speaker--busy' : ''}`} onClick={toggle} aria-label={busy ? 'Stop reading' : 'Read it to me'}>
          <span aria-hidden="true">{busy ? '◼' : '🔊'}</span>
        </button>
      )}
    </Tag>
  )
}

function wordLenAt(text, i) {
  const m = /^\S+/.exec(text.slice(i))
  return m ? m[0].length : 1
}
